import { useEffect, useState } from 'react';
import { getFlowBuilderRules } from '../../../axios/flow/index.api';
import { getAllProfessionals } from '../../../axios/professional/index.api';
import { getAllCategories } from '../../../axios/category/index.api';
import { getProfessionalServices } from '../../../axios/professionalServices/index.api';
import { FLOW_MODES, getFlowMode, normalizeFlags } from '../../../flow/flowFlags';

/**
 * What the admin "Create new appointment" modal needs to follow the
 * location's booking flow (Settings → Appointment → Appointment Journey):
 * the flow mode, which selectors to show (`blocks` from
 * `GET /flow/:locationId/rules`), the location's professionals and
 * categories, and — in "Professional first" — the picked professional's own
 * services. Back office sees every active professional/service (public or
 * not), like the Services page's "Back Office Visibility".
 */

/** Which sections the modal shows, in order, per booking flow. */
export const SECTIONS_BY_MODE = {
  [FLOW_MODES.SERVICE_ONLY]: ['service', 'dateTime', 'customer'],
  [FLOW_MODES.SERVICE_AND_PROFESSIONAL]: ['service', 'professional', 'dateTime', 'customer'],
  [FLOW_MODES.PROFESSIONAL_FIRST]: ['professional', 'professionalService', 'dateTime', 'customer']
};

export const MODE_LABELS = {
  [FLOW_MODES.SERVICE_ONLY]: 'Service only',
  [FLOW_MODES.SERVICE_AND_PROFESSIONAL]: 'Service + Professional',
  [FLOW_MODES.PROFESSIONAL_FIRST]: 'Professional first'
};

/**
 * @param {number|string} locationId
 * @param {boolean} open - loads when the modal opens
 */
export function useAppointmentJourney(locationId, open) {
  const [state, setState] = useState({
    loading: true,
    mode: FLOW_MODES.SERVICE_ONLY,
    blocks: { category: false, professional: false, services: true, professionalServices: false },
    professionals: [],
    categories: []
  });

  useEffect(() => {
    if (!open || !locationId) return undefined;
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    Promise.allSettled([
      getFlowBuilderRules(locationId),
      getAllProfessionals(locationId),
      getAllCategories(locationId)
    ]).then(([rules, professionals, categories]) => {
      if (cancelled) return;
      const flags = normalizeFlags(rules.status === 'fulfilled' ? rules.value?.flags : null);
      const mode = getFlowMode(flags);
      const blocks =
        rules.status === 'fulfilled' && rules.value?.blocks
          ? rules.value.blocks
          : {
              category: flags.isCategoryEnabled,
              professional: mode !== FLOW_MODES.SERVICE_ONLY,
              services: mode !== FLOW_MODES.PROFESSIONAL_FIRST,
              professionalServices: mode === FLOW_MODES.PROFESSIONAL_FIRST
            };
      setState({
        loading: false,
        mode,
        blocks,
        professionals:
          professionals.status === 'fulfilled'
            ? (professionals.value?.data?.data || []).filter((p) => p.isActive !== false)
            : [],
        categories:
          categories.status === 'fulfilled'
            ? (categories.value?.data?.data || []).filter((c) => c.status !== 'Inactive')
            : []
      });
    });
    return () => {
      cancelled = true;
    };
  }, [locationId, open]);

  return state;
}

/**
 * A professional's own live services ("Professional first"), loaded when
 * one is picked.
 */
export function useProfessionalServices(professionalId, locationId) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setServices([]);
    if (!professionalId || !locationId) return undefined;
    let cancelled = false;
    setLoading(true);
    getProfessionalServices(professionalId, locationId)
      .then((list) => {
        if (!cancelled) {
          setServices(
            (list || [])
              .filter((s) => s.isLive !== false)
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setServices([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [professionalId, locationId]);
  return { services, loading };
}
